import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Slider,
  IconButton,
  Box,
  Stack,
  useTheme
} from '@mui/material';
import { Add, Remove, DeviceThermostat } from '@mui/icons-material';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';

const ThermostatWidget = ({ 
  title = "Salon", 
  currentTemp = 21.5, 
  initialTarget = 20 
}) => {
  const theme = useTheme();
  // État local pour la température de consigne
  const [targetTemp, setTargetTemp] = useState(initialTarget);

  // Gestionnaires d'événements pour les contrôles
  const handleSliderChange = (event, newValue) => setTargetTemp(newValue);
  const decreaseTemp = () => setTargetTemp((prev) => Math.max(10, prev - 0.5));
  const increaseTemp = () => setTargetTemp((prev) => Math.min(30, prev + 0.5));

  // Logique visuelle : si on chauffe, l'arc devient rouge (error), sinon bleu (info)
  const isHeating = targetTemp > currentTemp;
  const gaugeColor = isHeating ? theme.palette.error.main : theme.palette.info.main;

  return (
    <Card sx={{ maxWidth: 360, borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        
        {/* 1. En-tête du Widget */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <DeviceThermostat color="action" />
          <Typography variant="h6" color="text.secondary" fontWeight="500">
            {title}
          </Typography>
        </Stack>

        {/* 2. Visualisation (La Jauge en arc de cercle) */}
        <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative', height: 160 }}>
          <Gauge
            width={260}
            height={160}
            value={targetTemp}
            valueMin={10} // Température minimum
            valueMax={30} // Température maximum
            startAngle={-110} // Ouvre l'arc de cercle
            endAngle={110}
            innerRadius="80%" // Donne un aspect d'anneau fin et élégant
            outerRadius="100%"
            text="" // On masque le texte par défaut de la jauge pour le faire nous-mêmes
            sx={{
              // Personnalisation de la couleur de l'arc de remplissage
              [`& .${gaugeClasses.valueArc}`]: {
                fill: gaugeColor,
                transition: 'fill 0.3s ease',
              },
            }}
          />
          
          {/* Typographie superposée au centre de la jauge */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -10%)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h3" fontWeight="bold" color="text.primary">
              {targetTemp}°
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Actuel : {currentTemp}°C
            </Typography>
          </Box>
        </Box>

        {/* 3. Contrôles (Actions utilisateur) */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2, px: 1 }}>
          <IconButton onClick={decreaseTemp} color="primary" size="large" aria-label="Baisser la température">
            <Remove />
          </IconButton>
          
          <Slider
            value={targetTemp}
            min={10}
            max={30}
            step={0.5}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            sx={{ 
              flexGrow: 1,
              // Optionnel : donner un peu plus d'épaisseur au slider pour le tactile
              '& .MuiSlider-thumb': { width: 24, height: 24 } 
            }}
          />
          
          <IconButton onClick={increaseTemp} color="primary" size="large" aria-label="Augmenter la température">
            <Add />
          </IconButton>
        </Stack>

      </CardContent>
    </Card>
  );
};

export default ThermostatWidget;